import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useRef } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import GoogleOAuthButton from "./GoogleOAuthButton";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useTurnstile } from "@/hooks/useTurnstile";
import { userApiClient, ApiException } from "@/lib/userApiClient";

/**
 * 国际登录面板
 * 支持：邮箱+密码、Google OAuth、Apple 登录
 */
interface InternationalLoginPanelProps {
  redirectTo?: string;
}

export default function InternationalLoginPanel({
  redirectTo = "/",
}: InternationalLoginPanelProps) {
  const { t } = useTranslation("login");
  const [isLoading, setIsLoading] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // 使用 useTurnstile Hook
  const {
    containerRef: turnstileRef,
    token: turnstileToken,
    isLoading: turnstileLoading,
    isAvailable: turnstileAvailable,
    reset: resetTurnstile,
  } = useTurnstile({
    theme: "auto",
    size: "flexible",
    onError: () => {
      console.warn("Turnstile verification failed");
    },
    onExpire: () => {
      console.warn("Turnstile token expired");
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const email = emailRef.current?.value.trim();
    const password = passwordRef.current?.value.trim();

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t("common.invalidEmail"));
      return;
    }

    if (!password) {
      toast.error(t("international.passwordLabel"));
      return;
    }

    if (turnstileAvailable && !turnstileToken) {
      toast.error(t("common.completeCaptcha", "请完成人机验证"));
      return;
    }

    setIsLoading(true);

    try {
      // 获取当前语言设置
      const currentLanguage = localStorage.getItem("i18nextLng") || "en";

      // 使用 API 登录
      await userApiClient.post(
        "/auth/login",
        {
          email,
          password,
          turnstile_token: turnstileToken,
        },
        {
          headers: {
            "Accept-Language": currentLanguage,
          },
        },
      );

      toast.success(t("common.loginSuccess"));
      setTimeout(() => {
        window.location.href = redirectTo;
      }, 500);
    } catch (error) {
      if (error instanceof ApiException) {
        if (
          error.code === "EMAIL_NOT_VERIFIED" &&
          error.details?.requires_verification
        ) {
          // 需要验证邮箱，保持 loading 状态防止重复点击
          toast.info(t("common.verifyEmailFirst", "请先验证您的邮箱"));
          setTimeout(() => {
            window.location.href = `/verify-email?email=${encodeURIComponent(error.details.email)}`;
          }, 500);
          return;
        }
        setIsLoading(false);
        resetTurnstile();
        toast.error(error.message || t("common.emailOrPasswordError"));
      } else {
        setIsLoading(false);
        console.error("Login failed:", error);
        toast.error(t("common.networkError"));
      }
    }
  };

  const handleAppleLogin = () => {
    toast.info(
      t("international.appleComingSoon", "Apple Sign-In coming soon..."),
    );
  };

  return (
    <div className="space-y-6">
      <form
        onSubmit={handleSubmit}
        className="space-y-4"
        aria-label={t("international.loginButton")}
        aria-busy={isLoading}
      >
        <div>
          <Input
            id="email"
            type="email"
            placeholder={t("international.emailPlaceholder")}
            ref={emailRef}
            autoComplete="email"
            aria-required="true"
            aria-label={t("international.emailLabel")}
            className="bg-background dark:bg-background h-10"
          />
        </div>

        <div className="space-y-2">
          <Input
            id="password"
            type="password"
            placeholder={t("international.passwordPlaceholder")}
            ref={passwordRef}
            autoComplete="current-password"
            aria-required="true"
            aria-label={t("international.passwordLabel")}
            className="bg-background dark:bg-background h-10"
          />
          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {t("international.forgotPassword")}
            </Link>
          </div>
        </div>

        {/* Turnstile - 使用 Hook */}
        {turnstileAvailable && (
          <div
            className="w-full"
            aria-label={t("common.captchaVerification", "人机验证")}
          >
            <div ref={turnstileRef} />
          </div>
        )}

        <Button
          type="submit"
          className="w-full"
          size="lg"
          disabled={
            isLoading ||
            turnstileLoading ||
            (turnstileAvailable && !turnstileToken)
          }
          aria-busy={isLoading}
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isLoading
            ? t("international.loggingIn")
            : t("international.loginButton")}
        </Button>
      </form>

      {/* 第三方登录 */}
      <div className="space-y-3">
        <GoogleOAuthButton
          region="global"
          variant="outline"
          redirectTo={redirectTo}
          onLoginStart={() => setIsLoading(true)}
          onError={() => setIsLoading(false)}
        />

        <Button
          type="button"
          variant="outline"
          size="lg"
          className="w-full"
          onClick={handleAppleLogin}
        >
          <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
          </svg>
          {t("international.appleLogin")}
        </Button>
      </div>
    </div>
  );
}
